/* =========================================================================
   Filter counts for a grid narrowed beyond Shopify's own filters.

   Shopify counts each Filters value over the collection with the applied
   filters — it knows nothing of what the page narrows on top: a Shop-by
   bubble (Occasion, Concern, Goal), the listing quiz's answer (?vroq=), a
   quiz answer carried in (?vro=). So the dropdown said "Oily (24)" over a
   grid of 6, and a value could say (3) and give nothing.

   This works the counts out from the products the grid can show:

     count(value V of filter G) = products that
       - are in the base collection,
       - pass the page's narrowing (opts.sets, opts.allow, opts.band),
       - pass every applied filter except G's own (values in one filter are
         an "or", as Shopify counts them), and the URL's price range,
       - and have V.

   Each product's values come from templates/collection.vro-filter-values
   .liquid, fetched once per collection and cached.

   Never worse than Shopify: before its own counts are used, a filter's
   counts are worked out WITHOUT the narrowing and checked against
   Shopify's for the same collection and filters
   (sections/vro-facet-counts.liquid). A filter that doesn't match exactly
   — a kind of filter the template can't read, or reads differently —
   keeps Shopify's counts.

   API (window.VroFacetCount):
     paint({ base, sets, allow, band }) -> Promise
       base   collection handle the grid is built from
       sets   [{handle: true}] — the grid keeps only products in all of these
       allow  {handle: true} | null — another such set (a quiz answer)
       band   {min, max} in rupees | null — a Shop-by price band
     reset()  un-hides the filters paint() hid
   ========================================================================= */
(function () {
  'use strict';
  if (window.VroFacetCount) return;

  var ROOT = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/');
  var MAX_PAGES = 12;
  var INPUTS = '#FacetFiltersForm input[type="checkbox"][name^="filter."], .plp-sheet-form input[type="checkbox"][name^="filter."]';
  var GROUPS = '#FacetFiltersForm .clp-filter-pill-wrap, .plp-sheet-form .plp-sheet-group';

  var valuesCache = {};
  var token = 0;

  function fetchText(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error(String(r.status));
      return r.text();
    });
  }

  function urlFilters() {
    var out = { groups: {}, gte: null, lte: null, query: [] };
    new URLSearchParams(window.location.search).forEach(function (v, k) {
      if (v === '' || k.indexOf('filter.') !== 0) return;
      out.query.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
      if (k === 'filter.v.price.gte') { out.gte = parseFloat(v); return; }
      if (k === 'filter.v.price.lte') { out.lte = parseFloat(v); return; }
      (out.groups[k] = out.groups[k] || []).push(v);
    });
    return out;
  }

  /* A collection's products and their filter values, every page. */
  function valuesOf(base) {
    if (valuesCache[base]) return valuesCache[base];
    var url = ROOT + 'collections/' + encodeURIComponent(base) + '?view=vro-filter-values';
    var job = fetchText(url + '&page=1').then(function (t) {
      var first = JSON.parse(t);
      var pages = Math.min(parseInt(first.pages, 10) || 1, MAX_PAGES);
      var rest = [];
      for (var p = 2; p <= pages; p++) rest.push(fetchText(url + '&page=' + p).then(JSON.parse));
      return Promise.all(rest).then(function (more) {
        var items = [];
        var seen = {};
        [first].concat(more).forEach(function (d) {
          (d.items || []).forEach(function (it) {
            if (!it || seen[it.h]) return;
            seen[it.h] = true;
            items.push(it);
          });
        });
        return { params: first.params || [], items: items };
      });
    });
    job.catch(function () { delete valuesCache[base]; });
    valuesCache[base] = job;
    return job;
  }

  /* Shopify's own counts for the collection with the URL's filters. */
  function nativeCounts(base, f) {
    var q = f.query.join('&');
    return fetchText(ROOT + 'collections/' + encodeURIComponent(base) + '?' + (q ? q + '&' : '') + 'section_id=vro-facet-counts')
      .then(function (html) {
        var node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-vro-facet-counts]');
        return node ? JSON.parse(node.textContent) : null;
      });
  }

  function has(list, value) {
    if (!list) return false;
    for (var i = 0; i < list.length; i++) {
      var raw = list[i];
      if (raw === value) return true;
      /* A metaobject filter's value is its gid; the template gives the id. */
      if (raw && value.length > raw.length && value.slice(-raw.length - 1) === '/' + raw) return true;
    }
    return false;
  }

  function inRange(prices, lo, hi) {
    if (lo === null && hi === null) return true;
    for (var i = 0; i < prices.length; i++) {
      var r = prices[i] / 100;
      if ((lo === null || r >= lo) && (hi === null || r <= hi)) return true;
    }
    return false;
  }

  /* count[param][value] for every input on the bar. */
  function countAll(data, f, inputs, opts) {
    var index = {};
    data.params.forEach(function (p, i) { index[p] = i; });
    var lo = f.gte, hi = f.lte;
    if (opts.band) {
      if (opts.band.min !== null && opts.band.min !== undefined) lo = lo === null ? opts.band.min : Math.max(lo, opts.band.min);
      if (opts.band.max !== null && opts.band.max !== undefined) hi = hi === null ? opts.band.max : Math.min(hi, opts.band.max);
    }
    var sets = opts.sets || [];
    var pool = data.items.filter(function (it) {
      for (var i = 0; i < sets.length; i++) if (!sets[i][it.h]) return false;
      if (opts.allow && !opts.allow[it.h]) return false;
      return inRange(opts.judged ? it.p : it.q, lo, hi);
    });
    /* Which applied filters each product passes. */
    var applied = Object.keys(f.groups);
    var passes = pool.map(function (it) {
      var ok = {};
      applied.forEach(function (g) {
        var i = index[g];
        if (i === undefined || it.f[i] === null) { ok[g] = true; return; }
        ok[g] = f.groups[g].some(function (v) { return has(it.f[i], v); });
      });
      return ok;
    });
    var out = {};
    inputs.forEach(function (input) {
      var g = input.name, v = input.value;
      out[g] = out[g] || {};
      if (out[g][v] !== undefined) return;
      var i = index[g];
      if (i === undefined) { out[g][v] = null; return; }
      var n = 0;
      pool.forEach(function (it, k) {
        if (it.f[i] === null) return;
        for (var a = 0; a < applied.length; a++) if (applied[a] !== g && !passes[k][applied[a]]) return;
        if (has(it.f[i], v)) n++;
      });
      out[g][v] = n;
    });
    return out;
  }

  function countEl(input) {
    var label = input.closest('label');
    if (!label) return null;
    var all = label.querySelectorAll('*');
    for (var i = all.length - 1; i >= 0; i--) {
      if (!all[i].children.length && /\(\d+\)\s*$/.test(all[i].textContent)) return all[i];
    }
    return null;
  }

  function write(input, n) {
    var el = countEl(input);
    if (el) el.textContent = el.textContent.replace(/\(\d+\)\s*$/, '(' + n + ')');
    var off = n === 0 && !input.checked;
    input.disabled = off;
    var label = input.closest('label');
    if (label) label.classList.toggle('is-disabled', off);
  }

  function hideEmpty() {
    document.querySelectorAll(GROUPS).forEach(function (group) {
      var boxes = group.querySelectorAll('input[type="checkbox"][name^="filter."]');
      if (!boxes.length) return;
      var live = Array.prototype.some.call(boxes, function (b) { return !b.disabled || b.checked; });
      if (live) {
        if (group.hasAttribute('data-vro-fc-hidden')) { group.style.display = ''; group.removeAttribute('data-vro-fc-hidden'); }
      } else {
        group.style.display = 'none';
        group.setAttribute('data-vro-fc-hidden', '');
      }
    });
  }

  function reset() {
    token++;
    document.querySelectorAll('[data-vro-fc-hidden]').forEach(function (g) {
      g.style.display = '';
      g.removeAttribute('data-vro-fc-hidden');
    });
  }

  function paint(opts) {
    var mine = ++token;
    var f = urlFilters();
    return Promise.all([valuesOf(opts.base), nativeCounts(opts.base, f)]).then(function (got) {
      if (mine !== token) return;
      var data = got[0], native = got[1];
      var inputs = Array.prototype.slice.call(document.querySelectorAll(INPUTS));
      /* The check: no narrowing, Shopify's price rule, against Shopify. */
      var plain = countAll(data, f, inputs, { judged: false });
      var trusted = {};
      inputs.forEach(function (input) {
        var g = input.name;
        if (trusted[g] === false) return;
        var mineN = plain[g] && plain[g][input.value];
        var theirs = native ? (native[g + '|' + input.value] || 0) : null;
        trusted[g] = mineN !== null && mineN !== undefined && theirs !== null && mineN === theirs;
      });
      var counts = countAll(data, f, inputs, {
        judged: true, sets: opts.sets, allow: opts.allow, band: opts.band
      });
      inputs.forEach(function (input) {
        var g = input.name;
        var n = trusted[g] ? counts[g][input.value]
          : (native ? (native[g + '|' + input.value] || 0) : null);
        if (n === null || n === undefined) return;
        write(input, n);
      });
      hideEmpty();
      if (window.console && console.debug) {
        var off = Object.keys(trusted).filter(function (g) { return !trusted[g]; });
        if (off.length) console.debug('[vro-facet-count] Shopify counts kept for', off.join(', '));
      }
    }).catch(function () { /* the counts already showing stay */ });
  }

  window.VroFacetCount = { paint: paint, reset: reset };
})();
